import { memo, useEffect, useReducer, useRef } from "react"
import { StateProp } from "../types"
import UserContext from "./context"
import { initial_user, userReducer } from "./reducer"
import { setUser, setUserInitialization, updateTokens } from "./actions"
import { updateUser } from "../../services/calls/users"
import { changeLanguage } from "../../services/utils/language"
import { getAvailableFleets } from "../../services/calls/fleet"
import { exchangeImpersonationCode } from "../../services/calls/auth"

const UserState = memo(({ children }: StateProp) => {
  const [user, dispatch] = useReducer(userReducer, initial_user)
  const initializing = useRef(false)

  const fetchUser = async () => {
    const fetchedUser = await updateUser()
    if (typeof fetchedUser === 'object') {
      const fleets = await getAvailableFleets()
      const newUser = {
        ...fetchedUser,
        available_fleets: fleets,
        name: `${fetchedUser.first_name} ${fetchedUser.last_name}`
      }
      dispatch(setUser(newUser))
      
      const { language } = newUser
      changeLanguage(language === 'en-gb' ? 'en' : 'is')
    }
    dispatch(setUserInitialization(true))
  }

  // Admin "Log in as user" lands on /impersonate#code=..., the code can only be used once
  const startImpersonation = async () => {
    const code = new URLSearchParams(window.location.hash.slice(1)).get('code')
    if (code) {
      try {
        const { access, refresh } = await exchangeImpersonationCode(code)
        localStorage.setItem('access_token', access)
        localStorage.setItem('refresh_token', refresh)
      } catch {
        // Expired or already used code, continue with whatever session was there
      }
    }
    // Reload so the router and user state start fresh with the new tokens
    window.location.replace('/')
  }

  useEffect(() => {
    // StrictMode runs effects twice in development
    if (initializing.current) return
    initializing.current = true

    const initialize = async () => {
      if (window.location.pathname === '/impersonate') {
        await startImpersonation()
        return
      }

      const oldAccessToken = await localStorage.getItem('access_token')
      const oldRefreshToken = await localStorage.getItem('refresh_token')

      if ((oldAccessToken && oldAccessToken !== 'undefined') || (oldRefreshToken && oldRefreshToken !== 'undefined')) {
        dispatch(updateTokens({ access: oldAccessToken, refresh: oldRefreshToken }))
      } else {
        dispatch(setUserInitialization(true)) // nothing stored to validate, so there's nothing to wait for
      }
    }

    initialize()
  }, [])

  useEffect(() => {
    if (user.access_token) {
      localStorage.setItem('access_token', user.access_token)
      if (user.refresh_token) localStorage.setItem('refresh_token', user.refresh_token)

      if (!user.username.length) fetchUser()
    } else {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
  }, [user.access_token, user.refresh_token])

  return (
    <UserContext.Provider value={{ user, dispatch }}>
      {children}
    </UserContext.Provider>
  )
})

export default UserState
